/**
 * Project detail — tabs estese: Overview, WBS, Team, Milestone, Ore, Gantt, Kanban,
 * Documenti, Storico, Economics.
 */
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Accordion, Alert, Anchor, Badge, Button, Card, FileButton, Grid, Group, Loader, Progress, Stack, Table, Tabs,
  Text, Title,
} from '@mantine/core';
import {
  IconArrowLeft, IconBuildingFactory2, IconCash, IconChartPie, IconClipboardList, IconClock,
  IconFileText, IconFiles, IconFlag, IconHistory, IconLayoutKanban, IconTimeline, IconUsers,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { fetchList, fetchOne } from '@/shared/api/jsonapi/client';
import { fetchProjectHoursSummary, fetchProjectTeam } from '@/features/projects/api';
import { FavoriteStar } from '@/features/projects/FavoriteStar';
import { HealthBadge, PriorityBadge, healthLabel } from '@/features/projects/HealthBadge';
import { ProjectEconomicsCard, type EconomicsData } from '@/features/projects/ProjectEconomicsCard';
import { ProjectMilestoneTimeline } from '@/features/projects/ProjectMilestoneTimeline';
import { ProjectWbsTree } from '@/features/projects/ProjectWbsTree';
import { ProjectGantt } from '@/features/projects/ProjectGantt';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface Project extends Denormalized {
  // Anagrafica
  name?: string;
  code?: string;
  status?: string;
  description?: string;
  customerName?: string;
  notes?: string;
  freeFieldString1?: string;
  freeFieldString2?: string;
  freeFieldString3?: string;
  priority?: string;
  healthStatus?: string;
  riskLevel?: string;
  // Date
  startDate?: string;
  endDate?: string;
  baselineStart?: string;
  baselineEnd?: string;
  actualStart?: string;
  actualEnd?: string;
  deadline?: string;
  // Economics
  plannedHours?: number;
  actualHours?: number;
  remainingHours?: number;
  budget?: number;
  budgetCost?: number;
  estimatedRevenue?: number;
  estimatedCost?: number;
  invoicedAmount?: number;
  marginEstimated?: number;
  marginActual?: number;
  orderNumber?: string;
  contractDate?: string;
  contractValue?: number;
  paymentTerms?: string;
  currency?: string;
  isBillable?: boolean;
  // Relationships
  customer?: { id: string; type?: string; businessName?: string; name?: string };
  category?: { id: string; type?: string; name?: string; code?: string };
  projectManager?: { id: string; type?: string; firstName?: string; lastName?: string; email?: string };
}

interface ProjectActivity extends Denormalized {
  type: 'project-activities';
  eventType?: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  description?: string;
  createdAt?: string;
}

export const Route = createFileRoute('/_app/projects/$projectId')({ component: ProjectDetailPage });

const STATUS_COLOR: Record<string, string> = {
  open: 'blue', 'in-progress': 'green', 'on-hold': 'yellow', closed: 'gray', archived: 'dark',
};

function ProjectDetailPage() {
  const { projectId } = useParams({ from: '/_app/projects/$projectId' });
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => fetchOne<Project>('project-entities', projectId, { include: ['customer', 'category'] }),
  });

  if (isLoading) return <Loader />;
  if (!data) return <Text c="dimmed">Commessa non trovata</Text>;

  const customerName = data.customer && 'businessName' in data.customer
    ? data.customer.businessName ?? data.customer.name
    : data.customerName;
  const categoryName = data.category && 'name' in data.category ? data.category.name : undefined;

  return (
    <Stack>
      <Anchor component={Link} to="/projects" size="sm">
        <Group gap={4}><IconArrowLeft size={14} /> Torna alle commesse</Group>
      </Anchor>
      <div>
        <Group justify="space-between" wrap="wrap">
          <Group>
            <FavoriteStar projectId={projectId} size={22} />
            <Title order={1}>{data.name}</Title>
            <Badge>{data.code}</Badge>
            <Badge color={STATUS_COLOR[data.status ?? ''] ?? 'gray'}>{data.status}</Badge>
            {data.priority && <PriorityBadge value={data.priority} />}
            <HealthBadge value={data.healthStatus} />
          </Group>
          <Group>
            <Button
              variant="light"
              onClick={() => void navigate({ to: `/projects/${projectId}/dashboard` })}
              leftSection={<IconChartPie size={16} />}
            >
              Dashboard
            </Button>
          </Group>
        </Group>
        {customerName && <Text c="dimmed">Cliente: <strong>{customerName}</strong>{categoryName ? ` · ${categoryName}` : ''}</Text>}
        {data.description && <Text c="dimmed">{data.description}</Text>}
      </div>

      <Tabs defaultValue="overview" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconBuildingFactory2 size={16} />}>Overview</Tabs.Tab>
          <Tabs.Tab value="wbs" leftSection={<IconClipboardList size={16} />}>WBS</Tabs.Tab>
          <Tabs.Tab value="team" leftSection={<IconUsers size={16} />}>Team</Tabs.Tab>
          <Tabs.Tab value="milestones" leftSection={<IconFlag size={16} />}>Milestone</Tabs.Tab>
          <Tabs.Tab value="hours" leftSection={<IconClock size={16} />}>Ore</Tabs.Tab>
          <Tabs.Tab value="gantt" leftSection={<IconTimeline size={16} />}>Gantt</Tabs.Tab>
          <Tabs.Tab value="kanban" leftSection={<IconLayoutKanban size={16} />}>Kanban</Tabs.Tab>
          <Tabs.Tab value="docs" leftSection={<IconFiles size={16} />}>Documenti</Tabs.Tab>
          <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>Storico</Tabs.Tab>
          <Tabs.Tab value="economics" leftSection={<IconCash size={16} />}>Economics</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <OverviewPanel project={data} />
        </Tabs.Panel>
        <Tabs.Panel value="wbs" pt="md"><ProjectWbsTree projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="team" pt="md"><TeamPanel projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="milestones" pt="md"><ProjectMilestoneTimeline projectId={projectId} currency={data.currency ?? 'EUR'} /></Tabs.Panel>
        <Tabs.Panel value="hours" pt="md"><HoursPanel projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="gantt" pt="md"><ProjectGantt projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="kanban" pt="md">
          <KanbanRedirectPanel projectId={projectId} />
        </Tabs.Panel>
        <Tabs.Panel value="docs" pt="md"><DocsPanel /></Tabs.Panel>
        <Tabs.Panel value="history" pt="md"><HistoryPanel projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="economics" pt="md">
          <EconomicsPanel project={data} projectId={projectId} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function OverviewPanel({ project }: { project: Project }) {
  return (
    <Grid>
      <Grid.Col span={{ base: 12, md: 8 }}>
        <Accordion defaultValue={['identity', 'dates', 'health']} multiple variant="separated">
          <Accordion.Item value="identity">
            <Accordion.Control>Anagrafica</Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                <FieldRow label="Codice" value={project.code} />
                <FieldRow label="Nome" value={project.name} />
                <FieldRow label="Cliente" value={project.customer && 'businessName' in project.customer ? project.customer.businessName : project.customerName} />
                <FieldRow label="Categoria" value={project.category && 'name' in project.category ? project.category.name : undefined} />
                <FieldRow label="Project Manager" value={project.projectManager ? `${project.projectManager.firstName ?? ''} ${project.projectManager.lastName ?? ''}`.trim() || project.projectManager.email || project.projectManager.id : undefined} />
                <FieldRow label="Descrizione" value={project.description} />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="dates">
            <Accordion.Control>Date &amp; baseline</Accordion.Control>
            <Accordion.Panel>
              <Grid>
                <Grid.Col span={6}><FieldRow label="Inizio" value={fmtDate(project.startDate)} /></Grid.Col>
                <Grid.Col span={6}><FieldRow label="Fine" value={fmtDate(project.endDate)} /></Grid.Col>
                <Grid.Col span={6}><FieldRow label="Baseline inizio" value={fmtDate(project.baselineStart)} /></Grid.Col>
                <Grid.Col span={6}><FieldRow label="Baseline fine" value={fmtDate(project.baselineEnd)} /></Grid.Col>
                <Grid.Col span={6}><FieldRow label="Effettivo inizio" value={fmtDate(project.actualStart)} /></Grid.Col>
                <Grid.Col span={6}><FieldRow label="Effettivo fine" value={fmtDate(project.actualEnd)} /></Grid.Col>
                <Grid.Col span={12}><FieldRow label="Deadline" value={fmtDate(project.deadline)} /></Grid.Col>
              </Grid>
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="health">
            <Accordion.Control>Health &amp; priorità</Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                <Group>
                  <Text fw={600} miw={140}>Priorità:</Text>
                  <PriorityBadge value={project.priority} />
                </Group>
                <Group>
                  <Text fw={600} miw={140}>Health:</Text>
                  <HealthBadge value={project.healthStatus} />
                </Group>
                <Group>
                  <Text fw={600} miw={140}>Rischio:</Text>
                  <Badge variant="light">{project.riskLevel ?? '—'}</Badge>
                </Group>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="notes">
            <Accordion.Control>Note</Accordion.Control>
            <Accordion.Panel>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{project.notes || '—'}</Text>
              {project.freeFieldString1 && (
                <Group mt="sm" gap={4}>
                  {project.freeFieldString1.split(',').filter(Boolean).map((tag) => (
                    <Badge key={tag} variant="dot">{tag.trim()}</Badge>
                  ))}
                </Group>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Card withBorder padding="lg" radius="md">
          <Stack>
            <Title order={5}>Stato salute</Title>
            <HealthBadge value={project.healthStatus} size="lg" />
            <Text c="dimmed" size="sm">{healthLabel(project.healthStatus)}</Text>
            {project.deadline && (
              <Alert color={dayjs(project.deadline).isBefore(dayjs()) ? 'red' : 'blue'}>
                Deadline: {fmtDate(project.deadline)}
              </Alert>
            )}
          </Stack>
        </Card>
      </Grid.Col>
    </Grid>
  );
}

function FieldRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <Group>
      <Text fw={600} miw={140}>{label}:</Text>
      <Text>{value != null && value !== '' ? value : '—'}</Text>
    </Group>
  );
}

function fmtDate(d?: string | null): string | null {
  if (!d) return null;
  return dayjs(d).format('DD/MM/YYYY');
}

function TeamPanel({ projectId }: { projectId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['project-team', projectId],
    queryFn: () => fetchProjectTeam(projectId) as Promise<Array<{ id: string; fullName?: string; role?: string; email?: string }>>,
  });
  if (isLoading) return <Loader />;
  const members = data ?? [];
  return (
    <Card withBorder padding={0} radius="md">
      <Table verticalSpacing="xs" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Nome</Table.Th>
            <Table.Th>Ruolo</Table.Th>
            <Table.Th>Email</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {members.length === 0 && (
            <Table.Tr><Table.Td colSpan={3}><Text c="dimmed" ta="center" py="lg">Nessun membro assegnato.</Text></Table.Td></Table.Tr>
          )}
          {members.map((m) => (
            <Table.Tr key={m.id}>
              <Table.Td>{m.fullName ?? m.id}</Table.Td>
              <Table.Td><Badge variant="light">{m.role ?? '—'}</Badge></Table.Td>
              <Table.Td>{m.email ?? '—'}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

function HoursPanel({ projectId }: { projectId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['project-hours-panel', projectId],
    queryFn: () => fetchProjectHoursSummary(projectId),
  });
  if (isLoading) return <Loader />;
  if (!data) return <Text c="dimmed">Nessun dato.</Text>;
  const usagePct = data.budget && data.budget > 0
    ? Math.min(100, (data.totalOrdinaryHours / data.budget) * 100)
    : null;
  return (
    <Card withBorder padding="lg" radius="md">
      <Stack>
        <Group justify="space-between">
          <Text c="dimmed">Ore ordinarie</Text>
          <Text fw={600}>{data.totalOrdinaryHours.toFixed(1)} h</Text>
        </Group>
        <Group justify="space-between">
          <Text c="dimmed">Ore fatturabili</Text>
          <Text fw={600}>{data.totalBillingHours.toFixed(1)} h</Text>
        </Group>
        <Group justify="space-between">
          <Text c="dimmed">Spese</Text>
          <Text fw={600}>€ {data.totalExpenses.toFixed(2)}</Text>
        </Group>
        <Group justify="space-between">
          <Text c="dimmed">Voci</Text>
          <Text fw={600}>{data.entriesCount}</Text>
        </Group>
        {usagePct !== null && (
          <Stack gap={4}>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Budget {data.totalOrdinaryHours.toFixed(1)}/{data.budget?.toFixed(1)}h</Text>
              <Text size="sm" fw={600} c={usagePct >= 90 ? 'red' : usagePct >= 75 ? 'orange' : 'green'}>{usagePct.toFixed(0)}%</Text>
            </Group>
            <Progress value={usagePct} color={usagePct >= 90 ? 'red' : usagePct >= 75 ? 'orange' : 'green'} />
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function KanbanRedirectPanel({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  return (
    <Card withBorder padding="lg" radius="md">
      <Stack align="center">
        <IconLayoutKanban size={48} color="var(--mantine-color-blue-6)" />
        <Title order={5}>Kanban della commessa</Title>
        <Text c="dimmed" ta="center">Apri la board kanban filtrata per i task di questa commessa.</Text>
        <Button onClick={() => void navigate({ to: '/tasks/kanban', search: { projectId } as never })}>
          Vai a Kanban
        </Button>
      </Stack>
    </Card>
  );
}

function DocsPanel() {
  return (
    <Card withBorder padding="lg" radius="md">
      <Stack>
        <Group justify="space-between">
          <Title order={5}>Documenti</Title>
          <FileButton onChange={() => undefined} accept="*/*" multiple>
            {(props) => <Button {...props} variant="light" leftSection={<IconFileText size={14} />}>Carica file</Button>}
          </FileButton>
        </Group>
        <Alert color="blue" variant="light">
          Integrazione con Media Library prossimamente. Per ora i file allegati alla commessa sono gestiti tramite la media library globale.
        </Alert>
        <Anchor component={Link} to="/media" size="sm">Apri Media Library →</Anchor>
      </Stack>
    </Card>
  );
}

function HistoryPanel({ projectId }: { projectId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['project-activities', projectId],
    queryFn: () => fetchList<ProjectActivity>('project-activities', {
      filter: { 'project.id': { eq: projectId } },
      sort: ['-createdAt'],
      page: { size: 100 },
    }),
  });
  if (isLoading) return <Loader />;
  const list = data?.data ?? [];
  if (list.length === 0) return <Text size="sm" c="dimmed">Nessuna attività registrata.</Text>;
  return (
    <Stack gap={6}>
      {list.map((a) => (
        <Card key={a.id} withBorder padding="xs" radius="sm">
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Group gap={6}>
                <Badge variant="light" size="xs">{a.eventType ?? 'event'}</Badge>
                {a.field && <Text size="xs" fw={600}>{a.field}</Text>}
              </Group>
              {a.description && <Text size="sm">{a.description}</Text>}
              {(a.oldValue || a.newValue) && (
                <Text size="xs">
                  {a.oldValue && <>da <Text span c="red">{a.oldValue}</Text></>}
                  {a.oldValue && a.newValue && ' '}
                  {a.newValue && <>a <Text span c="green">{a.newValue}</Text></>}
                </Text>
              )}
            </Stack>
            <Text size="xs" c="dimmed">{a.createdAt ? dayjs(a.createdAt).format('DD/MM/YY HH:mm') : ''}</Text>
          </Group>
        </Card>
      ))}
    </Stack>
  );
}

function EconomicsPanel({ project, projectId }: { project: Project; projectId: string }) {
  const hoursQ = useQuery({
    queryKey: ['project-hours-summary', projectId],
    queryFn: () => fetchProjectHoursSummary(projectId),
  });
  const econ: EconomicsData = {
    budget: project.budget,
    budgetCost: project.budgetCost,
    plannedHours: project.plannedHours,
    actualHours: project.actualHours,
    remainingHours: project.remainingHours,
    estimatedRevenue: project.estimatedRevenue,
    estimatedCost: project.estimatedCost,
    invoicedAmount: project.invoicedAmount,
    marginEstimated: project.marginEstimated,
    marginActual: project.marginActual,
    currency: project.currency,
    orderNumber: project.orderNumber,
    contractDate: project.contractDate,
    contractValue: project.contractValue,
    paymentTerms: project.paymentTerms,
  };
  return <ProjectEconomicsCard data={econ} hoursUsed={hoursQ.data?.totalOrdinaryHours} />;
}
